import { Button } from "@mui/material";
import cx from "classnames";
import { useMemo, useState } from "react";
import { Vector3 } from "three";
import { create } from "zustand";

import {
  type ArtifactStructure,
  type BoardEdge,
  type CatalogCard,
  MATERIAL_IDS,
  type MaterialId,
  type PieceName,
  type ResourceName,
  intents
} from "src/api";

import { MATERIAL_DISPLAY } from "../../constants";
import { useGame } from "../../game/GameContext";
import { PIECE_DEFINITIONS } from "../../game/board/worldObjects/pieces/pieceDefinitions";
import { STRUCTURE_DEFINITIONS } from "../../game/board/worldObjects/structures/structureDefinitions";
import { useSpawnSelection } from "../../game/spawnSelection";
import { OrbitalButtonSpec, OrbitalButtons } from "../shared/OrbitalButtons";
import "./DevMenu.css";

export type DevState = {
  cardDisplayOverrideEnabled: boolean;
  cardDisplayData?: readonly CatalogCard[];
  setCardDisplayOverride(
    enabled: boolean,
    cards?: readonly CatalogCard[]
  ): void;
};

export const useDevState = create<DevState>((set) => ({
  cardDisplayOverrideEnabled: false,
  setCardDisplayOverride: (enabled, cards) =>
    set({ cardDisplayOverrideEnabled: enabled, cardDisplayData: cards })
}));

export function DevMenu() {
  const { dev: testHandle, catalog, board, session } = useGame();
  const { cardDisplayData, setCardDisplayOverride } = useDevState();
  const selection = useSpawnSelection((state) => state.pieceOrStructure);
  const selectionType = selection?.kind === "piece" ? selection.piece : null;
  const [category, setCategory] = useState<string | null>(null);

  const [selectedEdge, setSelectedEdge] = useState<BoardEdge>("north");

  const boardToggles = useMemo<OrbitalButtonSpec[]>(
    () => [
      {
        content: (
          <div
            className={cx(
              "orbital-toggle",
              selectedEdge === "north" ? "selected" : null
            )}
            onClick={() => setSelectedEdge("north")}
          >
            N
          </div>
        ),
        vector: new Vector3(0, 0, -1)
      },
      {
        content: (
          <div
            className={cx(
              "orbital-toggle",
              selectedEdge === "south" ? "selected" : null
            )}
            onClick={() => setSelectedEdge("south")}
          >
            S
          </div>
        ),
        vector: new Vector3(0, 0, 1)
      },
      {
        content: (
          <div
            className={cx(
              "orbital-toggle",
              selectedEdge === "west" ? "selected" : null
            )}
            onClick={() => setSelectedEdge("west")}
          >
            W
          </div>
        ),
        vector: new Vector3(-1, 0, 0)
      },
      {
        content: (
          <div
            className={cx(
              "orbital-toggle",
              selectedEdge === "east" ? "selected" : null
            )}
            onClick={() => setSelectedEdge("east")}
          >
            E
          </div>
        ),
        vector: new Vector3(1, 0, 0)
      }
    ],
    [selectedEdge, setSelectedEdge]
  );

  // After every hook: a hook below an early return runs on some renders and
  // not others, which is the rules-of-hooks error CI failed on.
  if (!testHandle) return null;

  const expandBoard = () => {
    const command = { type: "dev-grow", edge: selectedEdge };
    testHandle.dispatch(command);
  };

  const collapseBoard = () => {
    const command = { type: "dev-collapse", edge: selectedEdge };
    testHandle.dispatch(command);
  };

  const addCards = () => {
    const cards = catalog.cards();
    const length = (cardDisplayData?.length ?? 0) + 5;
    setCardDisplayOverride(
      true,
      Array.from({ length }, (_, k) => cards[k % cards.length])
    );
  };

  const removeAllCards = () => {
    setCardDisplayOverride(false);
  };

  const giveMaterial = (material: MaterialId) => {
    const side = session?.view().side;
    const king = [...board.facts().squares.values()].find(
      (facts) => facts.piece?.type === "king" && facts.piece.side === side
    );
    if (!king) return;
    const i = MATERIAL_IDS.indexOf(material);
    const [resource, grade] = (
      i < 3 ? ["metal", i] : i < 6 ? ["stone", i - 3] : ["special", i - 6]
    ) as [ResourceName, number];
    testHandle.dispatch(
      intents.dev.material(king.square, { resource, grade, delta: 10 })
    );
  };

  const cheatItems: Record<
    string,
    { name: string; onClick: () => void; isSelected?: boolean }[]
  > = {
    Pieces: (Object.keys(PIECE_DEFINITIONS) as PieceName[]).map((name) => ({
      name: name,
      isSelected: name === selectionType,
      onClick: () =>
        useSpawnSelection.setState({
          pieceOrStructure:
            name === selectionType ? null : { kind: "piece", piece: name }
        })
    })),
    Materials: MATERIAL_IDS.map((id) => ({
      name: MATERIAL_DISPLAY[id].label,
      onClick: () => giveMaterial(id)
    })),
    Structures: (Object.keys(STRUCTURE_DEFINITIONS) as ArtifactStructure[]).map(
      (name) => ({
        name: name,
        onClick: () => testHandle.dispatch(intents.dev.grantToken(name))
      })
    ),
    Cards: catalog.cards().map((card) => ({
      name: card.name,
      onClick: () => testHandle.dispatch(intents.dev.grantCard(card.id))
    }))
  };

  return (
    <div className="dev-menu">
      <OrbitalButtons buttons={boardToggles} />
      <div>
        <Button size="small" onClick={expandBoard}>
          Expand
        </Button>
        <Button size="small" onClick={collapseBoard}>
          Collapse
        </Button>
        <Button size="small" onClick={addCards}>
          Add Cards
        </Button>
        <Button size="small" onClick={removeAllCards}>
          Clear Cards
        </Button>
      </div>
      {category && (
        <div className="dev-spawn-items">
          {cheatItems[category].map((item, i) => (
            <Button
              key={i}
              size="small"
              variant={item.isSelected ? "contained" : "outlined"}
              onClick={item.onClick}
            >
              {item.name}
            </Button>
          ))}
        </div>
      )}
      <div>
        {Object.keys(cheatItems).map((name) => (
          <Button
            key={name}
            size="small"
            variant={name === category ? "contained" : "text"}
            onClick={() => setCategory(name === category ? null : name)}
          >
            {name}
          </Button>
        ))}
      </div>
    </div>
  );
}
